import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { FaEye, FaFilePdf } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { ButtonCmp } from '../../../../../components/ButtonCmp'
import { ElementoTooltipCmp } from '../../../../../components/ElementoTooltipCmp'
import { SearchCmp } from '../../../../../components/SearchCmp'
import { SelectCmp } from '../../../../../components/SelectCmp'
import { WhiteContainer } from '../../../../../components/WhiteContainer'
import { appApi } from '../../../../../services/appApi'
import { baixarPdfVersao, iVersaoResumo, rotuloDaVersao } from '../../../Individual/components/HistoricoVersoes'
import { formatarDataHora } from '../../../utils/data'

interface iVersaoEmpresa extends iVersaoResumo {
  cargoId: string
  nomeCargo?: string
}

export const VersoesFechadas: React.FC = () => {
  const nav = useNavigate()
  const [Versoes, setVersoes] = useState<iVersaoEmpresa[]>([])
  const [Busca, setBusca] = useState('')
  const [FiltroAno, setFiltroAno] = useState('')
  const [FiltroCargo, setFiltroCargo] = useState('')
  const [Baixando, setBaixando] = useState<string>()
  const [SemAcesso, setSemAcesso] = useState(false)
  useEffect(() => {
    appApi.get<iVersaoEmpresa[]>('api/CargosESalarios/HistoricoVersoes')
      .then(res => setVersoes(res.data))
      .catch(err => {
        if (err?.response?.status === 403) setSemAcesso(true)
        console.log(err)
      })
  }, [])

  const anos = useMemo(() => Array.from(new Set(Versoes.map(v => v.ano))).sort((a, b) => b - a), [Versoes])
  const cargos = useMemo(() => {
    const mapa = new Map(Versoes.map(v => [v.cargoId, v.nomeCargo ?? '-']))
    return Array.from(mapa, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome))
  }, [Versoes])

  const filtradas = useMemo(() => {
    const termo = Busca.toLowerCase()
    return Versoes.filter(v => ((v.nomeCargo ?? '').toLowerCase().includes(termo) || rotuloDaVersao(v).toLowerCase().includes(termo)) &&
      (!FiltroAno || v.ano.toString() === FiltroAno) &&
      (!FiltroCargo || v.cargoId === FiltroCargo))
  }, [Versoes, Busca, FiltroAno, FiltroCargo])

  function baixar (versao: iVersaoEmpresa): void {
    setBaixando(versao.id)
    appApi.get(`api/CargosESalarios/HistoricoVersoes/${versao.cargoId}/${versao.id}`)
      .then(async res => await baixarPdfVersao(versao.cargoId, versao, res.data.snapshot))
      .catch(err => console.log(err))
      .finally(() => setBaixando(undefined))
  }

  if (SemAcesso) return null

  return (
    <WhiteContainer padding='1.5rem'>
      <Flex justifyContent={'space-between'} alignItems={'center'} mb={'1rem'} gap={'1rem'} flexWrap={'wrap'}>
        <Flex alignItems={'center'} gap={'.75rem'}>
          <h3>Versões fechadas</h3>
          <Text fontSize={'12px'} fontWeight={'700'} bg={'gray.100'} px={'.6rem'} py={'.2rem'} borderRadius={'full'}>{Versoes.length} versões</Text>
        </Flex>
      </Flex>
      <Flex gap={'1rem'} mb={'1rem'} alignItems={'end'} flexWrap={'wrap'}>
        <SearchCmp placeholder='Buscar por cargo ou versão...' width={'min(18rem, 100%)'} OnChange={setBusca} EnterAction={() => {}} />
        <SelectCmp title='Ano' w={{ base: '100%', sm: '10rem' }} value={FiltroAno} OnChange={setFiltroAno}>
          <option value=''>Todos</option>
          {anos.map(a => <option key={a} value={a}>{a}</option>)}
        </SelectCmp>
        <SelectCmp title='Cargo' w={{ base: '100%', sm: '16rem' }} value={FiltroCargo} OnChange={setFiltroCargo}>
          <option value=''>Todos</option>
          {cargos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </SelectCmp>
      </Flex>
      <Box overflowX={'auto'}>
        <Table size={'sm'} minW={'700px'}>
          <Thead>
            <Tr>
              <Th>Cargo</Th>
              <Th>Versão</Th>
              <Th>Fechada em</Th>
              <Th textAlign={'right'}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtradas.map(v => (
              <Tr key={v.id} _hover={{ bg: 'blue.50' }}>
                <Td fontWeight={'700'}>{v.nomeCargo ?? '-'}</Td>
                <Td>{rotuloDaVersao(v)}</Td>
                <Td>{formatarDataHora(v.dataFechamento) ?? '-'}</Td>
                <Td>
                  <Flex justifyContent={'end'} gap={'.4rem'}>
                    <ButtonCmp small OutlineColor={'Green2'} leftIcon={<FaFilePdf />} isLoading={Baixando === v.id} onClick={() => baixar(v)}>
                      Baixar PDF
                    </ButtonCmp>
                    <ElementoTooltipCmp bg={'var(--Azul)'} label={'Visualizar cargo'}>
                      <ButtonCmp small VarColor='Green2' onClick={() => nav(`/Descritivo/Cargos/${v.cargoId}`)}>
                        <FaEye />
                      </ButtonCmp>
                    </ElementoTooltipCmp>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {filtradas.length === 0 && <Text textAlign={'center'} py={'2rem'} color={'gray.500'}>Nenhuma versão fechada encontrada.</Text>}
      </Box>
    </WhiteContainer>
  )
}
